/** Row shape used when reconciling header company selection after a companies reload. */
export interface CompanySelectionRow {
  company: { id: string; name: string; kryspace_slug?: string | null };
  instance: { id: string; instance_name?: string | null };
}

export interface ResolvedCompanySelection {
  id: string;
  name: string;
  instance_id?: string;
  kryspace_slug?: string;
}

export type CompanySelectionResolution =
  | { action: 'set'; selection: ResolvedCompanySelection }
  | { action: 'clear' }
  | { action: 'keep' };

/**
 * Prefer central when auto-picking among several roster rows (ops slugs next).
 * Short timeouts on manifest/structure fetches remain the hang guardrail.
 */
const PREFERRED_AUTO_SELECT_SLUGS = [
  'krysalis_central',
  'krysalis_frontend_ops',
  'krysalis_backend_ops',
  'krysalis_prime_ops',
] as const;

function toSelection(row: CompanySelectionRow): ResolvedCompanySelection {
  return {
    id: row.company.id,
    name: row.company.name,
    instance_id: row.instance.id,
    kryspace_slug: row.company.kryspace_slug?.trim() || undefined,
  };
}

function pickDefaultCompany(validCompanies: CompanySelectionRow[]): CompanySelectionRow {
  for (const slug of PREFERRED_AUTO_SELECT_SLUGS) {
    const match = validCompanies.find(
      (row) => (row.company.kryspace_slug ?? '').trim() === slug
    );
    if (match) return match;
  }
  return validCompanies[0];
}

/**
 * Reconcile selected company after loading instances. Avoids clearing a valid operator
 * selection when a partial API refresh omits the current company row.
 */
export function resolveCompanySelectionAfterLoad(
  validCompanies: CompanySelectionRow[],
  currentSelectedId: string | null,
  previousSelection: ResolvedCompanySelection | null
): CompanySelectionResolution {
  if (validCompanies.length === 0) {
    if (previousSelection?.kryspace_slug || previousSelection?.id) {
      return { action: 'keep' };
    }
    return { action: 'clear' };
  }

  if (currentSelectedId) {
    const found = validCompanies.find((row) => row.company.id === currentSelectedId);
    if (found) {
      return { action: 'set', selection: toSelection(found) };
    }

    if (previousSelection?.id === currentSelectedId) {
      return { action: 'keep' };
    }

    return { action: 'set', selection: toSelection(pickDefaultCompany(validCompanies)) };
  }

  if (previousSelection?.id) {
    const stillPresent = validCompanies.find((row) => row.company.id === previousSelection.id);
    if (stillPresent) {
      return { action: 'set', selection: toSelection(stillPresent) };
    }
  }

  // Multi-company roster with no prior pick: auto-select a stable default so
  // Control Bridge / render surfaces are not stuck on "Select company".
  return { action: 'set', selection: toSelection(pickDefaultCompany(validCompanies)) };
}
